"use client";

import { Avatar } from "@opensea/next-ui-kit";
import { CenterAligned, FlexCenter, FlexColumn, ModalBody, ModalFooter, ModalFooterButton, ModalHeader, ModalHeaderTitle, TextBody, ToastDescription, ToastTitle, useToasts } from "@opensea/ui-kit";
import { SwapHoriz } from "@opensea/ui-kit/icons";
import { isAddressEqual } from "@opensea/utils";
import { useConnect, useDisconnect } from "@opensea/wallet";
import { captureException } from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { AccountSwitcherAccountItemsSkeleton } from "@/components/navigation/top-nav/account-switcher/AccountSwitcherAccountItemsSkeleton";
import { LinkedAccountSwitcherItem } from "@/components/navigation/top-nav/account-switcher/LinkedAccountSwitcherItem";
import { AnimatedLogo } from "../../../../../../packages/wallet/src/components/AnimatedLogo";
import { ProgressChip } from "../../../studio/create/ProgressChip";
import { useRenderUsdBalance } from "../use-render-usd-balance";
import { useConnectedAccountProfile } from "./use-connect-account-profile";
export function SwitchWalletsModalContent({
  fromConnector,
  address
}) {
  const t = useTranslations("SwitchWalletsModalContent");
  const canAutoDetectSwitch = fromConnector.supportsAutoSwitching;
  const {
    showErrorMessage
  } = useToasts();
  const {
    disconnect
  } = useDisconnect();
  const {
    connect
  } = useConnect({
    isLinking: true
  });
  const [hasDisconnected, setHasDisconnected] = useState(false);
  const resetConnection = async () => {
    try {
      await disconnect(fromConnector, {
        endSession: false
      });
    } finally {
      setHasDisconnected(true);
    }
  };
  // This is done in a separate effect to avoid a race condition where the solana connect hook is not updated yet
  useEffect(() => {
    const reconnect = async (retry = true) => {
      try {
        const newAddress = await connect(fromConnector, {
          switchOnConnect: false
        });
        if (isAddressEqual(newAddress, address)) {
          showErrorMessage(<FlexColumn>
              <ToastTitle>{t("addressDidNotChange")}</ToastTitle>
              <ToastDescription size="xs">
                {t("switchInExtension", {
                connectorName: fromConnector.name
              })}
              </ToastDescription>
            </FlexColumn>, {});
          return;
        }
      } catch (error) {
        if (retry) {
          await disconnect(fromConnector, {
            endSession: false
          });
          await reconnect(false);
          return;
        }
        showErrorMessage(t("failedToSwitchWallet"), {
          duration: 10_000
        });
        captureException(error, {
          extra: {
            connector: fromConnector
          },
          tags: {
            source: "SwitchWalletsModalContent"
          }
        });
      } finally {
        setHasDisconnected(false);
      }
    };
    if (hasDisconnected) {
      reconnect();
    }
  }, [connect, hasDisconnected]);
  const {
    profile
  } = useConnectedAccountProfile(address);
  const renderUsdBalance = useRenderUsdBalance();
  const getResetButtonText = () => {
    if (canAutoDetectSwitch) {
      if (address) {
        return t("reset");
      }
      return t("reconnect");
    }
    return t("confirm");
  };
  return <>
      <ModalHeader className="flex justify-center pt-7 pb-1">
        <CenterAligned className="gap-4">
          <CenterAligned className="gap-6">
            {/* Wallet icons with arrow */}
            <FlexCenter className="gap-4">
              <CenterAligned className="h-16 w-16 rounded-full bg-white">
                {fromConnector.icon && <Avatar size={48} src={fromConnector.icon} />}
              </CenterAligned>
              <SwapHoriz size={24} />
              <AnimatedLogo size={60} />
            </FlexCenter>

            <ModalHeaderTitle>
              {t("title", {
              connectorName: fromConnector.name
            })}
            </ModalHeaderTitle>
          </CenterAligned>
        </CenterAligned>
      </ModalHeader>

      <ModalBody className="text-center">
        <CenterAligned className="gap-6">
          <TextBody className="text-center" color="text-secondary">
            {canAutoDetectSwitch ? t("description", {
            connectorName: fromConnector.name
          }) : t("descriptionNoAutoSwitch", {
            connectorName: fromConnector.name
          })}
          </TextBody>

          {/* Connected account preview */}
          <div className="w-full rounded-lg border border-border-1 bg-bg-primary">
            {profile ? <LinkedAccountSwitcherItem address={profile.address} alreadyLinked avatarSize={32} connected={Boolean(address)} description={renderUsdBalance(profile?.portfolioSummary?.estimatedTokenValue?.usd ?? undefined)} displayName={profile?.displayName} imageUrl={profile?.imageUrl || undefined} isLink={false} isPrimary={false} onClick={undefined} showActions={false} showCheck={false} variant="full" /> : <AccountSwitcherAccountItemsSkeleton />}
          </div>

          {canAutoDetectSwitch && <>
              {/* Loading state */}
              <ProgressChip isLoading variant="default">
                <TextBody className="font-mono">
                  {t("switchInProgress")}
                </TextBody>
              </ProgressChip>
            </>}
        </CenterAligned>
      </ModalBody>

      <ModalFooter>
        <ModalFooterButton disabled={hasDisconnected} isLoading={hasDisconnected} onClick={resetConnection} variant={canAutoDetectSwitch && address ? "secondary" : "primary"}>
          {getResetButtonText()}
        </ModalFooterButton>
      </ModalFooter>
    </>;
}